import React, { useState, useMemo, useCallback } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { makeStyles } from "@material-ui/core/styles";
import { createFieldsPluginRecipients } from "../../../TemplateBuilder/documentEditor/createFieldsPluginRecipients";
import { FieldModel, Group } from "../../../TemplateBuilder/documentEditor/types";

// Import the styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const PDFJS_VERSION = "2.16.105"; // keep in sync with other components

interface DocumentEditorRecipientsProps {
	fileUrl: string;
	templateName?: string;
	fields: FieldModel[];
	setFields: (updater: (prev: FieldModel[]) => FieldModel[]) => void;
	currentPage?: number;
	setCurrentPage?: (page: number) => void;
	onNumPages?: (num: number) => void;
	fieldAssignments?: Record<string, string>;
	onFieldSelect?: (fieldId: string) => void;
	recipients?: Array<{ id: string; name: string; email: string }>;
	groups?: Group[];
}

const useStyles = makeStyles((theme) => ({
	documentEditorContainer: {
		flexDirection: "row",
		gap: 16,
		height: "100%", // Use full height of parent pdfViewerContainer
		overflow: "hidden",
	},
	pdfViewerContainer: {
		flex: 1,
		height: "100%",
		overflow: "auto",
		backgroundColor: "#f0f0f0",
	},
}));

const DocumentEditorRecipients: React.FC<DocumentEditorRecipientsProps> = ({
	fileUrl,
	templateName,
	fields,
	setFields,
	currentPage = 1,
	setCurrentPage,
	onNumPages,
	fieldAssignments = {},
	onFieldSelect,
	recipients = [],
	groups = [],
}) => {
	const classes = useStyles();
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const moveField = useCallback((id: string, newBounds: Partial<FieldModel['bounds']>) => {
		setFields((prev) =>
			prev.map((f) =>
				f.id === id ? { ...f, bounds: { ...f.bounds, ...newBounds } } : f
			)
		);
	}, [setFields]);

	// Create the fields plugin for recipients mode
	const fieldsPlugin = useMemo(
		() => createFieldsPluginRecipients(
			fields, 
			moveField, 
			setSelectedId, 
			selectedId, 
			undefined, // onContextMenu - not needed for recipients mode
			fieldAssignments, 
			onFieldSelect,
			recipients,
			groups
		),
		[fields, moveField, selectedId, fieldAssignments, onFieldSelect, recipients, groups]
	);

	// Create the default layout plugin
	const defaultLayoutPluginInstance = defaultLayoutPlugin({
		sidebarTabs: () => [],
	});

	// Handle document load
	const onDocumentLoad = (e: any) => {
		if (onNumPages && e.doc.numPages) {
			onNumPages(e.doc.numPages);
		}
	};

	return (
		<div className={classes.documentEditorContainer}>
			<div className={classes.pdfViewerContainer}>
				<Worker workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`}>
					<Viewer
						fileUrl={fileUrl}
						plugins={[defaultLayoutPluginInstance, fieldsPlugin]}
						onDocumentLoad={onDocumentLoad}
					/>
				</Worker>
			</div>
		</div>
	);
};

export default DocumentEditorRecipients;
